import { useEffect, useState } from "react";
import { Building2, Users, CalendarCheck, CreditCard, Percent, Crown, Megaphone, Receipt } from "lucide-react";
import api from "../../lib/api";
import { formatPrice } from "../../lib/utils";
import { Card } from "../../components/ui/Card";

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <Card className="p-4">
      <div className="mb-2 flex items-center gap-2 text-muted-foreground">
        <Icon className="h-4 w-4" />
        <span className="text-xs">{label}</span>
      </div>
      <p className="text-2xl font-bold text-slate-900">{value}</p>
      {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
    </Card>
  );
}

export default function Analytics() {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    api
      .get("/admin/analytics")
      .then(({ data }) => setData(data))
      .catch(() => setLoadError(true));
  }, []);

  if (loadError) {
    return <p className="text-sm text-destructive">Couldn't load analytics. Please refresh the page.</p>;
  }
  if (!data) return <p className="text-muted-foreground">Loading…</p>;

  return (
    <div>
      <h1 className="mb-6 font-montserrat text-2xl font-bold text-slate-900">Platform analytics</h1>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={Building2}
          label="Businesses"
          value={data.businesses.total}
          sub={`${data.businesses.approved} approved · ${data.businesses.pending} pending`}
        />
        <StatCard icon={Users} label="Customers" value={data.customers} />
        <StatCard
          icon={CalendarCheck}
          label="Bookings"
          value={data.bookings.total}
          sub={`${data.bookings.completed} completed`}
        />
        <StatCard
          icon={CreditCard}
          label="Payment failure rate"
          value={`${data.payments.failure_rate_pct}%`}
          sub={`${data.payments.total} total payments`}
        />
      </div>

      {data.earnings && (
        <>
          <h2 className="mb-4 mt-8 font-montserrat text-lg font-bold text-slate-900">SHUWA earnings</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              icon={Percent}
              label="SHUWA booking fees"
              value={formatPrice(data.earnings.booking_fees)}
              sub={`${formatPrice(data.earnings.booking_fees_upcoming)} on upcoming bookings`}
            />
            <StatCard
              icon={Crown}
              label="Pro plans"
              value={formatPrice(data.earnings.pro_subscriptions)}
              sub={`${data.earnings.pro_businesses} businesses on Pro`}
            />
            <StatCard icon={Megaphone} label="Boost commissions" value={formatPrice(data.earnings.boost_commissions)} />
            <StatCard
              icon={Receipt}
              label="Owed by businesses"
              value={formatPrice(data.earnings.owed_by_businesses)}
              sub="Fees collected in person + Boost, not yet paid"
            />
          </div>
        </>
      )}
    </div>
  );
}
